import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronRight, ChevronLeft, X } from 'lucide-react'
import type { TourStep } from './tour-steps'

interface SpotlightTourProps {
  steps: TourStep[]
  active: boolean
  onComplete: () => void
  onSkip: () => void
}

interface Rect {
  top: number; left: number; width: number; height: number
}

const PADDING = 8 // px around the highlighted element

export default function SpotlightTour({ steps, active, onComplete, onSkip }: SpotlightTourProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [targetRect, setTargetRect] = useState<Rect | null>(null)
  const rafRef = useRef<number>(0)

  const step = steps[currentStep]

  // Track the target element's position (handles scroll/resize)
  useEffect(() => {
    if (!active || !step) return

    // Small delay to let the DOM settle after step change
    const timer = setTimeout(() => {
      const update = () => {
        const el = document.querySelector(step.target)
        if (el) {
          const r = el.getBoundingClientRect()
          // Only set rect if element is visible (has dimensions)
          if (r.width > 0 && r.height > 0) {
            setTargetRect({
              top: r.top - PADDING,
              left: r.left - PADDING,
              width: r.width + PADDING * 2,
              height: r.height + PADDING * 2,
            })
          } else {
            setTargetRect(null)
          }
        } else {
          setTargetRect(null)
        }
        rafRef.current = requestAnimationFrame(update)
      }
      update()
    }, 100)

    return () => {
      clearTimeout(timer)
      cancelAnimationFrame(rafRef.current)
    }
  }, [active, step])

  // Keyboard navigation
  useEffect(() => {
    if (!active) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSkip()
      if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext()
      if (e.key === 'ArrowLeft') handlePrev()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [active, currentStep])

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1)
    } else {
      onComplete()
    }
  }, [currentStep, steps.length, onComplete])

  const handlePrev = useCallback(() => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1)
  }, [currentStep])

  if (!active || !step) return null

  // Tooltip position
  const tooltip = computeTooltipPosition(targetRect, step.placement)

  return (
    <div className="fixed inset-0 z-[100]">
      {/* Backdrop with cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left}
                y={targetRect.top}
                width={targetRect.width}
                height={targetRect.height}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(3,7,18,0.75)"
          mask="url(#spotlight-mask)"
          style={{ pointerEvents: 'auto' }}
          onClick={onSkip}
        />
      </svg>

      {/* Highlight ring */}
      {targetRect && (
        <motion.div
          className="absolute rounded-lg border-2 border-cyan-400/50 pointer-events-none"
          animate={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          style={{ boxShadow: '0 0 20px rgba(6,182,212,0.2)' }}
        />
      )}

      {/* Tooltip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="absolute z-10 w-72 rounded-xl border border-cyan-500/20 bg-[#0a1628]/95 backdrop-blur-sm p-4"
          style={{ top: tooltip.top, left: tooltip.left }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] text-cyan-400/60 tracking-widest uppercase">
              {currentStep + 1} / {steps.length}
            </span>
            <button onClick={onSkip} className="text-slate-600 hover:text-slate-300 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Content */}
          <h3 className="font-mono text-sm text-slate-100 mb-1">{step.title}</h3>
          <p className="font-mono text-xs text-slate-500 leading-relaxed mb-4">{step.description}</p>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="flex items-center gap-1 font-mono text-[10px] text-slate-500 hover:text-slate-300 disabled:opacity-0 transition-all"
            >
              <ChevronLeft className="w-3 h-3" /> Back
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg font-mono text-xs bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25 transition-all"
            >
              {currentStep < steps.length - 1 ? (
                <>Next <ChevronRight className="w-3 h-3" /></>
              ) : (
                'Done'
              )}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

function computeTooltipPosition(
  rect: Rect | null,
  placement: TourStep['placement'],
): { top: number; left: number } {
  if (!rect) return { top: window.innerHeight / 2 - 100, left: window.innerWidth / 2 - 144 }

  const gap = 12
  const tooltipW = 288 // w-72 = 18rem = 288px
  const tooltipH = 160 // approximate

  switch (placement) {
    case 'bottom':
      return {
        top: rect.top + rect.height + gap,
        left: Math.min(Math.max(rect.left + rect.width / 2 - tooltipW / 2, 12), window.innerWidth - tooltipW - 12),
      }
    case 'top':
      return {
        top: rect.top - tooltipH - gap,
        left: Math.min(Math.max(rect.left + rect.width / 2 - tooltipW / 2, 12), window.innerWidth - tooltipW - 12),
      }
    case 'right':
      return {
        top: Math.min(Math.max(rect.top + rect.height / 2 - tooltipH / 2, 12), window.innerHeight - tooltipH - 12),
        left: rect.left + rect.width + gap,
      }
    case 'left':
      return {
        top: Math.min(Math.max(rect.top + rect.height / 2 - tooltipH / 2, 12), window.innerHeight - tooltipH - 12),
        left: rect.left - tooltipW - gap,
      }
  }
}
